// Commenting UI (M4): per-unit comment buttons, the comment form, replies, the review bar.
import { createContext, useContext, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import type { PlacedThread } from '../review/threads';
import { parseMarker } from '../review/threads';
import type { PrDetails } from '../review/api';
import { linesOf, neutralizeSuggestions, SUGGESTION_WARNING } from '../review/compose';
import { reviewCommand, type AddResult, type ReviewEvent, type ReviewSession } from '../review/session';
import type { BlockUnits, CommentUnit } from '../review/units-for-comments';
import { CloseIcon, CommentIcon } from './icons';

/** Everything the commenting widgets need, provided by the review page. */
export type ReviewCtx = {
  pr: PrDetails;
  session: ReviewSession;
  path: string;
  head: string;
  /** Reload PR details (threads, pending review) after a change. */
  refresh: () => Promise<void>;
  notify: (message: string) => void;
};
export const ReviewContext = createContext<ReviewCtx | null>(null);
export const useReview = () => {
  const c = useContext(ReviewContext);
  if (!c) throw new Error('ReviewContext missing');
  return c;
};

const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const UNIT_NOUN: Record<CommentUnit['kind'], string> = { block: 'this block', item: 'list item', 'table-head': 'table header', 'table-row': 'table row', table: 'table' };
const short = (t: string) => (t.length > 40 ? `${t.slice(0, 40)}…` : t);

/**
 * Comment buttons for the units of one display block. For lists and tables, one button per
 * list item / table row, aligned with it (measured), so the block still renders as a whole.
 */
/** The head line a thread is about (FILE threads: their Hashquill marker). */
function threadLine(t: PlacedThread): number {
  if (t.subjectType === 'FILE') return (t.comments[0] && parseMarker(t.comments[0].body)?.startLine) ?? 1;
  return t.line ?? t.originalLine ?? 1;
}

export function UnitButtons({ units: { units, block }, contentRef, threads = [], renderThreads }: {
  units: BlockUnits; contentRef: RefObject<HTMLDivElement | null>;
  /** Existing threads on this block: shown next to the unit they're about when units are aligned. */
  threads?: PlacedThread[]; renderThreads?: (threads: PlacedThread[]) => ReactNode;
}) {
  const [tops, setTops] = useState<number[] | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  const multi = units.length > 1;

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el || !multi) return;
    const measure = () => {
      const kind = units[0]!.kind;
      const targets = kind === 'item' ? el.querySelectorAll('li') : el.querySelectorAll('tr');
      if (targets.length !== units.length) { setTops(null); return; } // structure differs: one button for the block
      const base = el.getBoundingClientRect().top;
      setTops([...targets].map((t) => t.getBoundingClientRect().top - base));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [contentRef, units, multi]);

  // Per-unit buttons when they can be aligned; otherwise one button for the whole block.
  const shown: { unit: CommentUnit; top: number }[] = multi && tops
    ? units.map((unit, i) => ({ unit, top: tops[i]! }))
    : [{ unit: multi ? block : units[0]!, top: 0 }];

  // Threads by unit (outdated ones, or ones outside every unit, stay with the first).
  const byUnit = shown.map(() => [] as PlacedThread[]);
  for (const t of threads) {
    const line = threadLine(t);
    const i = t.outdated || shown.length === 1 ? 0 : Math.max(0, shown.findIndex(({ unit }) => line >= unit.startLine && line <= unit.endLine));
    byUnit[i]!.push(t);
  }

  return (
    <div className="hq-unitbtns">
      {shown.map(({ unit, top }, i) => (
        <div key={i} className="hq-unitbtn" style={{ top }}>
          {byUnit[i]!.length > 0 && renderThreads?.(byUnit[i]!)}
          <button type="button" className="hq-addbtn" aria-expanded={open === i}
            aria-label={unit.kind === 'block' ? `Comment on “${short(unit.text)}”` : `Comment on ${UNIT_NOUN[unit.kind]}: ${short(unit.text)}`}
            onClick={() => setOpen(open === i ? null : i)}>+</button>
          {open === i && <CommentForm unit={unit} onClose={() => setOpen(null)} />}
        </div>
      ))}
    </div>
  );
}

function CommentForm({ unit, onClose }: { unit: CommentUnit; onClose: () => void }) {
  const { session, path, head, refresh, notify } = useReview();
  const [comment, setComment] = useState('');
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [confirmNeutralize, setConfirmNeutralize] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = useId();
  const ref = useRef<HTMLFormElement>(null);
  const fileLevel = unit.anchor.kind === 'file';
  const canSuggest = unit.anchor.kind === 'line' && unit.anchor.wholeUnit;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async () => {
    if (!comment.trim() && suggestion === null) return;
    // File-level comments can't carry suggestions: warn once before sending.
    if (fileLevel && neutralizeSuggestions(comment).changed && !confirmNeutralize) { setConfirmNeutralize(true); return; }
    setBusy(true); setError(null);
    try {
      const r: AddResult = await session.addComment({
        path, anchor: unit.anchor, comment, quote: unit.text, unitStart: unit.startLine, unitEnd: unit.endLine,
        ...(suggestion !== null ? { suggestion } : {}),
      });
      if (r.fellBack) notify(`GitHub didn’t accept a comment on these lines, so it was added to the whole file instead.${r.suggestionNeutralized ? ` ${SUGGESTION_WARNING}` : ''}`);
      else if (r.suggestionNeutralized) notify(SUGGESTION_WARNING);
      await refresh();
      onClose();
    } catch (e) {
      setError(errMsg(e)); // keep the user's text in the form
    } finally {
      setBusy(false);
    }
  };

  return (
    <form ref={ref} className="hq-commentform" aria-label="New comment" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
      <button type="button" className="hq-iconbtn hq-popover__close" aria-label="Close" onClick={onClose}><CloseIcon size={14} /></button>
      <label htmlFor={`${id}-c`} className="hq-field-label">Comment</label>
      <textarea id={`${id}-c`} rows={3} autoFocus value={comment} onChange={(e) => { setComment(e.target.value); setConfirmNeutralize(false); }} />
      {fileLevel && (
        <p className="hq-note">These lines aren’t part of the changes, so GitHub only accepts this as a comment on the whole file. Hashquill remembers which text it’s about.</p>
      )}
      {suggestion === null && canSuggest && (
        <button type="button" className="hq-linkbtn" onClick={() => setSuggestion(linesOf(head, (unit.anchor as { startLine: number }).startLine, (unit.anchor as { line: number }).line))}>
          Suggest a change
        </button>
      )}
      {suggestion === null && !canSuggest && !fileLevel && (
        <p className="hq-note">To suggest a change, comment on a smaller part that’s entirely within the changes.</p>
      )}
      {suggestion !== null && (
        <div className="hq-field">
          <span className="hq-field-row"><label htmlFor={`${id}-s`} className="hq-field-label">Suggested change</label>
            <button type="button" className="hq-linkbtn" onClick={() => setSuggestion(null)}>Remove</button></span>
          <textarea id={`${id}-s`} className="hq-mono" rows={Math.min(10, suggestion.split('\n').length + 1)} value={suggestion} onChange={(e) => setSuggestion(e.target.value)} />
          <p className="hq-note">Replaces exactly these lines. The author can commit it on GitHub.</p>
        </div>
      )}
      {confirmNeutralize && <p className="hq-callout" role="alert">{SUGGESTION_WARNING}</p>}
      {error && <p className="hq-error" role="alert">Couldn’t add the comment: {error}. Your text is still here.</p>}
      <div className="hq-btn-row hq-btn-row--end">
        <button type="button" className="hq-btn hq-btn--secondary hq-btn--small" onClick={onClose}>Cancel</button>
        <button type="submit" className="hq-btn hq-btn--small" disabled={busy || (!comment.trim() && suggestion === null)}>
          {busy ? 'Adding…' : confirmNeutralize ? 'Add anyway' : 'Add to review'}
        </button>
      </div>
    </form>
  );
}

/** Reply box inside a thread popover. */
export function ReplyBox({ threadId, fileThread }: { threadId: string; fileThread: boolean }) {
  const { session, refresh, notify } = useReview();
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pending = !!session.pendingReviewId;
  const send = async () => {
    if (!body.trim()) return;
    setBusy(true); setError(null);
    try {
      const r = await session.reply(threadId, body, fileThread);
      if (r.suggestionNeutralized) notify(SUGGESTION_WARNING);
      setBody('');
      await refresh();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <form className="hq-reply" onSubmit={(e) => { e.preventDefault(); void send(); }}>
      <textarea aria-label="Reply" rows={2} placeholder="Reply…" value={body} onChange={(e) => setBody(e.target.value)} />
      {fileThread && neutralizeSuggestions(body).changed && <p className="hq-note">{SUGGESTION_WARNING}</p>}
      {error && <p className="hq-error" role="alert">Couldn’t reply: {error}</p>}
      <div className="hq-btn-row hq-btn-row--end">
        <button type="submit" className="hq-btn hq-btn--small" disabled={busy || !body.trim()}>
          {busy ? 'Sending…' : pending ? 'Add reply to review' : 'Reply'}
        </button>
      </div>
      {!pending && <p className="hq-note">Posted right away. Replies you add while a review is in progress are sent with it.</p>}
    </form>
  );
}

/** Sticky review bar (design/screens/02-review.html). */
export function ReviewBar({ pr, session, refresh, notice }: { pr: PrDetails; session: ReviewSession; refresh: () => Promise<void>; notice: string | null }) {
  const pending = pr.threads.flatMap((t) => t.comments).filter((c) => c.pending && c.author === pr.viewer);
  const suggestions = pending.filter((c) => /^\s*(`{3,}|~{3,})\s*suggestion/im.test(c.body)).length;
  const own = pr.viewer === pr.author;
  const [event, setEvent] = useState<ReviewEvent>('COMMENT');
  const [summary, setSummary] = useState('');
  const [showSummary, setShowSummary] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const needsText = event !== 'APPROVE' && pending.length === 0 && !summary.trim();
  const submit = async () => {
    if (needsText) { setShowSummary(true); setError(event === 'COMMENT' ? 'Add a comment or a summary first.' : 'Say what needs to change in the summary, or add comments.'); return; }
    setBusy(true); setError(null);
    try {
      await session.submit(event, summary);
      setSummary(''); setShowSummary(false);
      setDone(event === 'APPROVE' ? 'Review submitted: approved.' : event === 'REQUEST_CHANGES' ? 'Review submitted: changes requested.' : 'Review submitted.');
      await refresh();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };
  const discard = async () => {
    if (!confirm(`Discard your review? ${pending.length} pending ${pending.length === 1 ? 'comment' : 'comments'} will be deleted.`)) return;
    setBusy(true); setError(null);
    try { await session.discard(); await refresh(); setDone('Review discarded.'); } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="hq-reviewbar" role="region" aria-label="Your review">
      <div className="hq-reviewbar__status">
        <span><strong>Your review</strong> · {pending.length} pending {pending.length === 1 ? 'comment' : 'comments'}{suggestions ? `, ${suggestions} ${suggestions === 1 ? 'suggestion' : 'suggestions'}` : ''}</span>
        <span className="hq-note" aria-live="polite">{error ? <span className="hq-error">{error}</span> : notice ?? done ?? (pending.length ? 'Not sent yet. Submit to post your comments.' : 'Add comments with + next to any paragraph, list item or table row.')}</span>
      </div>
      {showSummary && (
        <textarea className="hq-reviewbar__summary" aria-label="Review summary" rows={2} placeholder="Summary (optional)" value={summary} onChange={(e) => setSummary(e.target.value)} />
      )}
      <div className="hq-reviewbar__actions">
        {!showSummary && <button type="button" className="hq-linkbtn" onClick={() => setShowSummary(true)}>Add a summary</button>}
        <fieldset className="hq-outcomes">
          <legend className="hq-visually-hidden">Review outcome</legend>
          {(['COMMENT', 'APPROVE', 'REQUEST_CHANGES'] as const).map((e) => (
            <label key={e} title={own && e !== 'COMMENT' ? 'You can’t approve or request changes on your own pull request.' : undefined}>
              <input type="radio" name="outcome" checked={event === e} disabled={own && e !== 'COMMENT'} onChange={() => setEvent(e)} />
              {e === 'COMMENT' ? 'Comment' : e === 'APPROVE' ? 'Approve' : 'Request changes'}
            </label>
          ))}
        </fieldset>
        {pending.length > 0 && <button type="button" className="hq-btn hq-btn--secondary" onClick={() => void discard()} disabled={busy}>Discard</button>}
        <div className="hq-action">
          <button type="button" className="hq-btn" onClick={() => void submit()} disabled={busy}>{busy ? 'Submitting…' : 'Submit review'}</button>
          <span className="hq-git">{reviewCommand(pr.number, event)}</span>
        </div>
      </div>
    </div>
  );
}

export { CommentIcon };
